// frontend/src/pages/FeaturesPage/FeaturesPage.jsx

import React, { useCallback, useState, useContext, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Particles from 'react-tsparticles';
import { loadSlim } from 'tsparticles-slim';
import { useTranslation } from 'react-i18next';
import { Box, Typography, List, ListItem, ListItemText, Tooltip } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import UniversalNav from '../../components/UniversalNav/UniversalNav';
import { UIContext } from '../../contexts/UIContext'

// --- ICONS FOR MANUAL TIMELINE ---
import PermMediaIcon from '@mui/icons-material/PermMedia'; 
import SearchIcon from '@mui/icons-material/Search';       
import CloudUploadIcon from '@mui/icons-material/CloudUpload'; 
import PercentIcon from '@mui/icons-material/Percent';     

// --- ICONS FOR API TIMELINE (Matched to Animations) ---
import CodeIcon from '@mui/icons-material/Code'; // Step 1 
import StorageIcon from '@mui/icons-material/Storage'; // Step 2 (High Volume/Stack)
import MemoryIcon from '@mui/icons-material/Memory'; // Step 3 (Processing)
import ApiIcon from '@mui/icons-material/Api'; // Step 4 (API)

// --- ICONS FOR MANUAL ANIMATIONS ---
import TextFieldsIcon from '@mui/icons-material/TextFields';
import ImageIcon from '@mui/icons-material/Image';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import VideocamIcon from '@mui/icons-material/Videocam';
import PsychologyIcon from '@mui/icons-material/Psychology'; 
import NewspaperIcon from '@mui/icons-material/Newspaper'; 
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'; 
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'; 
import TouchAppIcon from '@mui/icons-material/TouchApp'; 
import PersonIcon from '@mui/icons-material/Person'; 
import GppGoodIcon from '@mui/icons-material/GppGood'; 

// --- ADDITIONAL ICONS FOR API VISUALS ---
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

// --- IMAGES ---
import imgFeatureAnalyze from './assets/feature-analyze.webp'; 
import imgFeatureDetect from './assets/feature-detect.webp'; 
import imgFeatureQr from './assets/feature-qr.webp';
import imgFeatureApi from './assets/feature-api.webp'; 
import imgLaptop from './assets/laptop.webp';

import './FeaturesPage.css';

// --- MANUAL FLOW ANIMATIONS ---

// STEP 1: CONTENT TYPE
const ScreenAnimation1 = () => {
    const { t } = useTranslation();
    const gridStyle = {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gridTemplateRows: '1fr 1fr',
        gap: '8px',
        width: '90%', 
        height: '90%',
        boxSizing: 'border-box'
    };
    const wrapperStyle = {
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(23, 31, 41, 0.6)', borderRadius: '6px', 
        width: '100%', height: '100%',
        border: '1.5px solid var(--fp-secondary)', cursor: 'pointer', zIndex: 20, 
        boxSizing: 'border-box'
    };
    const iconStyle = { fontSize: 'clamp(18px, 3.5vw, 28px)', color: 'var(--fp-secondary)' };
    const icons = [
        { Icon: TextFieldsIcon, label: t('featuresPage.animations.analyzeText') }, 
        { Icon: ImageIcon,      label: t('featuresPage.animations.analyzeImage') },  
        { Icon: AudiotrackIcon, label: t('featuresPage.animations.analyzeAudio') },  
        { Icon: VideocamIcon,   label: t('featuresPage.animations.analyzeVideo') },   
    ];
    
    return (
        <div className="fp-anim-container">
            <div style={gridStyle}>
                {icons.map((item, index) => (
                    <Tooltip key={index} title={item.label} placement="top" arrow>
                        <motion.div
                            initial={{ opacity: 0, scale: 0 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.6, type: 'spring', bounce: 0.4, delay: index * 0.1 }}
                            style={wrapperStyle}
                            role="img"
                        >
                            <item.Icon style={iconStyle} />
                        </motion.div>
                    </Tooltip>
                ))}
            </div>
        </div>
    );
};


// STEP 2: TASKS
const ScreenAnimation2 = () => {
    const { t } = useTranslation();
    return (
        <div className="fp-anim-container">
            <motion.div
                initial={{ x: -80, opacity: 0, scale: 0.5 }}
                animate={{ x: [-80, -80, 0, 0], opacity: [0, 1, 1, 0], scale: [0.5, 1, 1, 0.8] }} 
                transition={{ duration: 3, times: [0, 0.1, 0.8, 1] }}
                style={{ position: 'absolute', zIndex: 1 }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: 'var(--fp-secondary)' }}>
                    <PsychologyIcon style={{ fontSize: 'clamp(40px, 10vw, 80px)' }} /> 
                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>{t('featuresPage.animations.aiDetection')}</Typography>
                </Box>
            </motion.div>
            <motion.div
                initial={{ x: 80, opacity: 0, scale: 0.5 }}
                animate={{ x: [80, 80, 0, 0], opacity: [0, 1, 1, 0], scale: [0.5, 1, 1, 0.8] }} 
                transition={{ duration: 3, times: [0, 0.1, 0.8, 1] }}
                style={{ position: 'absolute', zIndex: 2 }} 
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: 'var(--fp-secondary)' }}>
                    <NewspaperIcon style={{ fontSize: 'clamp(40px, 10vw, 80px)' }} /> 
                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>{t('featuresPage.animations.fakeNews')}</Typography>
                </Box>
            </motion.div>
            <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 2.4, type: 'spring', stiffness: 200 }} 
                style={{ position: 'absolute', zIndex: 20, display: 'flex', flexDirection: 'column', alignItems: 'center' }}
            >
                <VerifiedUserIcon style={{ fontSize: 'clamp(50px, 15vw, 100px)', color: 'var(--fp-secondary)' }} />
                <Typography variant="body1" sx={{ color: '#fff', fontWeight: 'bold', mt: 1 }}>{t('featuresPage.animations.vetraScore')}</Typography>
            </motion.div>
        </div>
    );
};

// STEP 3: DRAG & DROP
const ScreenAnimation3 = () => {
    const { t } = useTranslation();
    return (
        <div className="fp-anim-container">
            <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: [0, 1, 1, 0] }} 
                transition={{ duration: 3, times: [0, 0.2, 0.8, 1] }}
                style={{
                    position: 'absolute', width: '80%', height: '60%', 
                    border: '2px dashed rgba(255,255,255,0.3)', borderRadius: '8px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.05)',
                    boxSizing: 'border-box'
                }}
            >
                 <Typography variant="caption" sx={{color:'rgba(255,255,255,0.4)', textAlign: 'center'}}>
                     {t('featuresPage.animations.dropFile')}
                 </Typography>
            </motion.div>
            
            {/* Starts at 100% (hidden below the screen edge) and slides into view! */}
            <motion.div
                initial={{ top: '100%', scale: 0.8, opacity: 0 }}
                animate={{ top: ['100%', '100%', '35%', '35%'], opacity: [0, 1, 1, 0], scale: [1, 1, 1, 0] }}
                transition={{ duration: 3.5, times: [0, 0.2, 0.8, 1], ease: "easeInOut" }}
                style={{ position: 'absolute', zIndex: 10, color: 'white' }}
            >
                <InsertDriveFileIcon style={{ fontSize: 'clamp(26px, 6vw, 45px)' }} />
            </motion.div>
            
            <motion.div
                initial={{ top: '110%', opacity: 0 }}
                animate={{ top: ['110%', '110%', '45%', '45%'], opacity: [0, 1, 1, 0], scale: [1, 0.9, 0.9, 1] }}
                transition={{ duration: 3.5, times: [0, 0.2, 0.8, 1], ease: "easeInOut" }}
                style={{ position: 'absolute', zIndex: 11, color: 'var(--fp-secondary)' }}
            >
                <TouchAppIcon style={{ fontSize: 'clamp(35px, 8vw, 55px)', filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.8))' }} />
            </motion.div>
            
            <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 3 }}
                style={{ 
                    position: 'absolute', background: '#171f29', border: '1px solid var(--fp-secondary)',
                    padding: '6px 12px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '6px'
                }}
            >
                <InsertDriveFileIcon style={{ color: 'var(--fp-secondary)', fontSize: '1rem' }} />
                <Typography variant="body2" sx={{ color: 'white', fontSize: '0.75rem' }}>
                    {t('featuresPage.animations.fileName')}
                </Typography>
            </motion.div>
        </div>
    );
};

// STEP 4: SCAN
const ScreenAnimation4 = () => {
    const { t } = useTranslation();
    return (
        <div className="fp-anim-container">
            <motion.div
                initial={{ x: 0, color: 'rgba(255,255,255,0.3)' }}
                animate={{ x: [0, 0, -60], color: ['rgba(255,255,255,0.3)', 'rgba(255,255,255,0.3)', '#66bb6a'] }}
                transition={{ duration: 2, times: [0, 0.7, 1] }}
                style={{ position: 'absolute', zIndex: 5 }}
            >
                <PersonIcon style={{ fontSize: 'clamp(60px, 15vw, 100px)' }} />
            </motion.div>
            <motion.div
                initial={{ y: -50, opacity: 0 }}
                animate={{ y: 50, opacity: [0, 1, 1, 0] }}
                transition={{ duration: 1.2, ease: "linear", delay: 0.2 }}
                style={{
                    position: 'absolute', width: '80%', height: '2px',
                    background: 'var(--fp-secondary)', boxShadow: '0 0 8px var(--fp-secondary)', zIndex: 10
                }}
            />
            <motion.div
                initial={{ x: 60, opacity: 0 }}
                animate={{ x: 60, opacity: 1 }}
                transition={{ delay: 1.8 }}
                style={{ 
                    position: 'absolute', zIndex: 20,
                    display: 'flex', flexDirection: 'column', alignItems: 'center'
                }}
            >
                <GppGoodIcon style={{ fontSize: 'clamp(40px, 10vw, 60px)', color: '#66bb6a', marginBottom: '8px' }} />
                <Typography variant="caption" style={{ color: '#66bb6a', letterSpacing: '1px', fontWeight: 'bold' }}>
                    {t('featuresPage.animations.vetra')}
                </Typography>
            </motion.div>
        </div>
    );
};

// --- API FLOW ANIMATIONS (UPDATED) ---

const ScreenAnimationApi1 = () => {
    return (
        <div className="fp-anim-container" style={{flexDirection: 'column'}}>
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                style={{ display: 'flex', gap: '15px', alignItems: 'center' }}
            >
                <CodeIcon style={{ fontSize: 'clamp(30px, 8vw, 50px)', color: 'white' }} />
                <motion.div
                    animate={{ x: [0, 5, 0], opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                >
                     <ArrowForwardIcon style={{ fontSize: 'clamp(20px, 5vw, 30px)', color: 'var(--fp-secondary)' }} />
                </motion.div>
                <StorageIcon style={{ fontSize: 'clamp(30px, 8vw, 50px)', color: 'var(--fp-secondary)' }} />
            </motion.div>
            <Typography variant="body2" sx={{ mt: 2, color: 'rgba(255,255,255,0.7)', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                API_KEY: ***********
            </Typography>
        </div>
    );
};

const ScreenAnimationApi2 = () => {
    const { t } = useTranslation();
    return (
        <div className="fp-anim-container" style={{ flexDirection: 'column' }}>
            {/* Flying files */}
            {[...Array(5)].map((_, i) => (
                <motion.div
                    key={i}
                    initial={{ x: -100, y: Math.random() * 40 - 20, opacity: 0 }}
                    animate={{ x: 100, opacity: [0, 1, 1, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.3, ease: "linear" }}
                    style={{ position: 'absolute' }}
                >
                    <InsertDriveFileIcon style={{ fontSize: 20, color: 'rgba(255,255,255,0.4)' }} />
                </motion.div>
            ))}
            
            {/* Reduced icon size to fit shortened screen */}
            <StorageIcon style={{ fontSize: 'clamp(40px, 10vw, 60px)', color: 'var(--fp-secondary)', zIndex: 10 }} />
            
            {/* Positioned closer to the bottom edge */}
            <Typography variant="caption" sx={{ position: 'absolute', bottom: '5%', color: 'white', fontWeight: 'bold' }}>
                {t('featuresPage.animations.payload')}
            </Typography>
        </div>
    );
};

const ScreenAnimationApi3 = () => {
    const { t } = useTranslation();
    return (
        <div className="fp-anim-container" style={{ flexDirection: 'column' }}>
            <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
            >
                {/* Reduced icon size */}
                <MemoryIcon style={{ fontSize: 'clamp(50px, 12vw, 70px)', color: 'var(--fp-secondary)' }} />
            </motion.div>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: [0, 1, 1, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
                style={{ position: 'absolute', bottom: '5%' }}
            >
                <Typography variant="body2" sx={{ color: 'white', fontSize: '0.75rem' }}>{t('featuresPage.animations.processing')}...</Typography>
            </motion.div>
        </div>
    );
};

const ScreenAnimationApi4 = () => {
    const { t } = useTranslation();
    return (
        <div className="fp-anim-container" style={{flexDirection: 'column', justifyContent: 'center'}}>
            <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring' }}
            >
                {/* Reduced icon size */}
                <ApiIcon style={{ fontSize: 'clamp(40px, 10vw, 60px)', color: 'var(--fp-secondary)' }} />
            </motion.div>
            <motion.div
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                style={{ 
                    marginTop: '8px', /* Reduced margin */
                    background: 'rgba(255,255,255,0.05)', 
                    padding: '4px 12px', 
                    borderRadius: '6px', 
                    border: '1px solid var(--fp-secondary)'
                }}
            >
                <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'white', fontSize: '0.7rem' }}>
                    {t('featuresPage.animations.webhook')}
                </Typography>
            </motion.div>
        </div>
    );
};

// Variants
const containerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.15 } } };
const itemVariants = { hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1, transition: { duration: 0.5 } } };
const stepsContainerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.3, delayChildren: 0.2 } } };
const stepItemVariants = { hidden: { x: -20, opacity: 0 }, visible: { x: 0, opacity: 1, transition: { duration: 0.5 } } };

const FeaturesPage = () => {
    const { t } = useTranslation();
    const { openDocViewer } = useContext(UIContext);
    const [workflowMode, setWorkflowMode] = useState('manual'); // 'manual' | 'api'
    const [activeStep, setActiveStep] = useState(1);
    const [isScrolled, setIsScrolled] = useState(false);
    const containerRef = useRef(null);

    const handleScroll = (e) => {
        if (e.target.scrollTop > 20) {
            setIsScrolled(true);
        } else {
            setIsScrolled(false);
        }
    };
    
    // Particle background logic
    const particlesInit = useCallback(async (engine) => {
        await loadSlim(engine);
    }, []);

    const particlesConfig = {
        background: { color: { value: 'transparent' } },
        fpsLimit: 120,
        interactivity: { events: { onHover: { enable: true, mode: 'repulse' }, resize: true }, modes: { repulse: { distance: 100, duration: 0.4 } } },
        particles: { color: { value: '#ffffff' }, links: { color: '#4fc3f7', distance: 150, enable: true, opacity: 0.4, width: 1 }, move: { direction: 'none', enable: true, outModes: { default: 'bounce' }, random: false, speed: 1, straight: false }, number: { density: { enable: true, area: 800 }, value: 80 }, opacity: { value: 0.5 }, shape: { type: 'circle' }, size: { value: { min: 1, max: 3 } } },
        detectRetina: true,
    };

    // Manual Steps Data
    const manualSteps = [
        { id: 1, icon: <PermMediaIcon />, titleKey: 'featuresPage.howToUse.manualSteps.step1.primary', descKey: 'featuresPage.howToUse.manualSteps.step1.secondary' },
        { id: 2, icon: <SearchIcon />, titleKey: 'featuresPage.howToUse.manualSteps.step2.primary', descKey: 'featuresPage.howToUse.manualSteps.step2.secondary' },
        { id: 3, icon: <CloudUploadIcon />, titleKey: 'featuresPage.howToUse.manualSteps.step3.primary', descKey: 'featuresPage.howToUse.manualSteps.step3.secondary' },
        { id: 4, icon: <PercentIcon />, titleKey: 'featuresPage.howToUse.manualSteps.step4.primary', descKey: 'featuresPage.howToUse.manualSteps.step4.secondary' },
    ];

    // API Steps Data
    const apiSteps = [
        { id: 1, icon: <CodeIcon />, titleKey: 'featuresPage.howToUse.apiSteps.step1.primary', descKey: 'featuresPage.howToUse.apiSteps.step1.secondary' },
        { id: 2, icon: <StorageIcon />, titleKey: 'featuresPage.howToUse.apiSteps.step2.primary', descKey: 'featuresPage.howToUse.apiSteps.step2.secondary' },
        { id: 3, icon: <MemoryIcon />, titleKey: 'featuresPage.howToUse.apiSteps.step3.primary', descKey: 'featuresPage.howToUse.apiSteps.step3.secondary' },
        { id: 4, icon: <ApiIcon />, titleKey: 'featuresPage.howToUse.apiSteps.step4.primary', descKey: 'featuresPage.howToUse.apiSteps.step4.secondary' },
    ];

    const currentSteps = workflowMode === 'manual' ? manualSteps : apiSteps;

    return (
        <div 
            ref={containerRef}
            onScroll={handleScroll}
            className={`fp-page-container ${isScrolled ? 'scrolled' : ''}`}
        > 
            <Particles id="tsparticles-features" init={particlesInit} options={particlesConfig} />
            <Helmet>
                <title>{t('featuresPage.title')}</title>
                <meta name="description" content="Explore Vetra AI forensic analysis and API capabilities." />
            </Helmet>
            
            <div className="fp-nav-container">
                <UniversalNav />
            </div>

            <main className="fp-content-wrapper">
                <Box className="fp-glass-container">
                    
                    {/* Header */}
                    <Box className="fp-header">
                        <Typography variant="h3" component="h1">
                            {t('featuresPage.title')}
                        </Typography>
                        <Typography variant="subtitle1" component="p">
                            {t('featuresPage.subtitle')}
                        </Typography>
                    </Box>

                    <Box className="fp-section">
                        <Typography variant="h5" component="h2">{t('featuresPage.whatIs.title')}</Typography>
                        <Typography paragraph>
                            {t('featuresPage.whatIs.paragraph')}
                        </Typography>
                    </Box>
                    
                    {/* --- HERO CARDS --- */}
                    <motion.div 
                        className="fp-hero-grid"
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-100px" }}
                    >
                        <motion.div variants={itemVariants} className="fp-hero-card">
                            <img src={imgFeatureAnalyze} alt="Analyze" className="fp-card-image" />
                            <div className="fp-card-overlay">
                                <Typography variant="h5" component="h3">{t('featuresPage.cards.analyze.title')}</Typography>
                                <Typography variant="body2">{t('featuresPage.cards.analyze.text')}</Typography>
                            </div>
                        </motion.div>
                        
                        <motion.div variants={itemVariants} className="fp-hero-card">
                            <img src={imgFeatureDetect} alt="Detect" className="fp-card-image" />
                            <div className="fp-card-overlay">
                                <Typography variant="h5" component="h3">{t('featuresPage.cards.detect.title')}</Typography>
                                <Typography variant="body2">{t('featuresPage.cards.detect.text')}</Typography>
                            </div>
                        </motion.div>
                        
                        <motion.div variants={itemVariants} className="fp-hero-card">
                            <img src={imgFeatureQr} alt="QR Verify" className="fp-card-image" />
                            <div className="fp-card-overlay">
                                <Typography variant="h5" component="h3">{t('featuresPage.cards.qrCode.title')}</Typography>
                                <Typography variant="body2">{t('featuresPage.cards.qrCode.text')}</Typography>
                            </div>
                        </motion.div>

                        <motion.div variants={itemVariants} className="fp-hero-card">
                            <img src={imgFeatureApi} alt="API" className="fp-card-image" />
                            <div className="fp-card-overlay">
                                <Typography variant="h5" component="h3">{t('featuresPage.cards.apiIntegration.title')}</Typography>
                                <Typography variant="body2">{t('featuresPage.cards.apiIntegration.text')}</Typography>
                            </div>
                        </motion.div>
                    </motion.div>
                    
                    {/* --- INTERACTIVE HOW TO --- */}
                    <Box className="fp-section">
                        <Typography variant="h5" component="h2" className="fp-section-title">
                            {t('featuresPage.howToUse.title')}
                        </Typography>

                        {/* WORKFLOW TOGGLE */}
                        <div className="fp-workflow-toggle">
                            <button 
                                className={`fp-toggle-btn ${workflowMode === 'manual' ? 'active' : ''}`}
                                onClick={() => { setWorkflowMode('manual'); setActiveStep(1); }}
                            >
                                {t('featuresPage.howToUse.toggle.manual')}
                            </button>
                            <button 
                                className={`fp-toggle-btn ${workflowMode === 'api' ? 'active' : ''}`}
                                onClick={() => { setWorkflowMode('api'); setActiveStep(1); }}
                            >
                                {t('featuresPage.howToUse.toggle.api')}
                            </button>
                        </div>

                        <div className="fp-split-layout">
                            {/* LEFT COLUMN: Timeline */}
                            <motion.div 
                                key={workflowMode}
                                className="fp-steps-container"
                                variants={stepsContainerVariants}
                                initial="hidden"
                                animate="visible"
                            >
                                {currentSteps.map((step) => {
                                    let lineClass = '';
                                    if (activeStep === step.id) lineClass = 'line-active-start';
                                    else if (activeStep === step.id + 1) lineClass = 'line-active-end'; 
                                    else lineClass = 'line-inactive';

                                    return (
                                        <motion.div 
                                            key={step.id}
                                            variants={stepItemVariants}
                                            className={`fp-step-item ${activeStep === step.id ? 'active' : ''} ${lineClass}`}
                                            onMouseEnter={() => setActiveStep(step.id)}
                                        >
                                            <div className="fp-step-number">{step.id}</div>
                                            <div className="fp-glass-card-mini fp-step-content-box">
                                                <div className="fp-step-icon">
                                                    {step.icon}
                                                </div>
                                                <Box>
                                                    <Typography variant="h6" component="h3" className="fp-step-title">
                                                        {t(step.titleKey)}
                                                    </Typography>
                                                    <Typography variant="body2" className="fp-step-desc">
                                                        {t(step.descKey)}
                                                    </Typography>
                                                </Box>
                                            </div>
                                        </motion.div>
                                    );
                                })}
                            </motion.div>

                            {/* RIGHT COLUMN: Laptop Stage */}
                            <div className="fp-visual-container">
                                <div className="fp-laptop-stage">
                                    <img src={imgLaptop} alt="Laptop" className="fp-laptop-image" />
                                    
                                    <div className="fp-laptop-screen-area">
                                        <AnimatePresence mode="wait">
                                            {workflowMode === 'manual' && activeStep === 1 && (
                                                <motion.div key="m1" className="fp-anim-wrapper"><ScreenAnimation1 /></motion.div>
                                            )}
                                            {workflowMode === 'manual' && activeStep === 2 && (
                                                <motion.div key="m2" className="fp-anim-wrapper"><ScreenAnimation2 /></motion.div>
                                            )}
                                            {workflowMode === 'manual' && activeStep === 3 && (
                                                <motion.div key="m3" className="fp-anim-wrapper"><ScreenAnimation3 /></motion.div>
                                            )}
                                            {workflowMode === 'manual' && activeStep === 4 && (
                                                <motion.div key="m4" className="fp-anim-wrapper"><ScreenAnimation4 /></motion.div>
                                            )}

                                            {workflowMode === 'api' && activeStep === 1 && (
                                                <motion.div key="a1" className="fp-anim-wrapper"><ScreenAnimationApi1 /></motion.div>
                                            )}
                                            {workflowMode === 'api' && activeStep === 2 && (
                                                <motion.div key="a2" className="fp-anim-wrapper"><ScreenAnimationApi2 /></motion.div>
                                            )}
                                            {workflowMode === 'api' && activeStep === 3 && (
                                                <motion.div key="a3" className="fp-anim-wrapper"><ScreenAnimationApi3 /></motion.div>
                                            )}
                                            {workflowMode === 'api' && activeStep === 4 && (
                                                <motion.div key="a4" className="fp-anim-wrapper"><ScreenAnimationApi4 /></motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Box>

                    <AnimatePresence>
                        {workflowMode === 'api' && (
                            <motion.div 
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                style={{ textAlign: 'center', marginBottom: '2rem' }}
                            >
                                <button 
                                    className="fp-toggle-btn" 
                                    style={{ border: '1px solid var(--fp-secondary)', color: 'var(--fp-secondary)' }}
                                    onClick={(e) => {
                                        e.stopPropagation(); 
                                        openDocViewer('api/api_reference.md');
                                    }}
                                >
                                    {t('featuresPage.buttons.viewApiDocs')}
                                </button>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Who Benefits Section */}
                    <Box className="fp-section">
                        <Typography variant="h5" component="h2">{t('featuresPage.whoBenefits.title')}</Typography>
                        <Typography paragraph>
                            {t('featuresPage.whoBenefits.paragraph')}
                        </Typography>
                        <List>
                            <ListItem><ListItemText primary={t('featuresPage.whoBenefits.items.individuals.primary')} secondary={t('featuresPage.whoBenefits.items.individuals.secondary')} /></ListItem>
                            <ListItem><ListItemText primary={t('featuresPage.whoBenefits.items.creators.primary')} secondary={t('featuresPage.whoBenefits.items.creators.secondary')} /></ListItem>
                            <ListItem><ListItemText primary={t('featuresPage.whoBenefits.items.orgs.primary')} secondary={t('featuresPage.whoBenefits.items.orgs.secondary')} /></ListItem>
                        </List>
                    </Box>
                </Box>
            </main>
        </div>
    );
};

export default FeaturesPage;